import { useEffect, useRef } from 'react'
import { useSortable } from '@dnd-kit/react/sortable'
import { ChevronDown, ChevronUp, GripVertical, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { OPTION_DRAG_TYPE, optionSortableId } from '@/lib/formBuilderDnd'
import type { SelectOption } from './types'

/**
 * One row of a select field's inline options list: drag handle, label input,
 * up/down reorder arrows, remove (z8nrz7djxb).
 *
 * Its own component because `useSortable` is a hook and the list renders N of
 * them — the same reason `CanvasField` is separate from `FormCanvas`.
 */
interface Props {
  option: SelectOption
  /** Owning field — carried in the drag data so the page's dragover/dragend
   *  handlers can find this list without re-parsing the group id. */
  fieldId: string
  index: number
  /** Total rows, for disabling `down` on the last one (AC2.4). */
  count: number
  /** Sortable group — `optionGroupId(field.id)`, scoped per field so an option
   *  row and a canvas field can never resolve into one another (AC1.6). */
  group: string
  onLabelChange: (label: string) => void
  onMove: (from: number, to: number) => void
  onRemove: () => void
  /** Remove is disabled on the last remaining option, matching the pre-existing
   *  Add/Remove behaviour this ticket must not alter (AC5.1). */
  removeDisabled: boolean
  /** Read-only once the form version has submissions (AC3.7). */
  locked: boolean
}

export function InlineOptionRow({
  option,
  fieldId,
  index,
  count,
  group,
  onLabelChange,
  onMove,
  onRemove,
  removeDisabled,
  locked,
}: Props) {
  const { ref, handleRef, isDragging } = useSortable({
    id: optionSortableId(option.id),
    index,
    group,
    // `accept` stops a CANVAS FIELD being dropped onto an option row. It does
    // not stop the reverse — a canvas droppable sets no `accept`, so it accepts
    // anything, including an option drag. The guard that actually holds AC1.6 is
    // `isOptionRowTarget`, applied in the page's handlers; this is just the half
    // dnd-kit can enforce for us.
    type: OPTION_DRAG_TYPE,
    accept: [OPTION_DRAG_TYPE],
    // `origin` is what routes this drag to the option branch of the page's
    // handlers, which key every branch off it.
    data: { origin: 'option', optionId: option.id, fieldId },
    disabled: locked,
  })

  const isFirst = index === 0
  const isLast = index === count - 1

  const upRef = useRef<HTMLButtonElement>(null)
  const downRef = useRef<HTMLButtonElement>(null)
  const pendingFocus = useRef<'up' | 'down' | null>(null)

  /**
   * Keep focus on the arrow that was just pressed, so an option can be walked
   * up the list with repeated presses instead of re-acquiring focus after each
   * one (AC2.6).
   *
   * Not left to the browser: a reorder makes React move this row's DOM node
   * with `insertBefore`, and whether a focused element survives being moved is
   * inconsistent across browsers. Re-focusing is a no-op when focus did
   * survive, so this makes the behaviour the same either way — and it cannot be
   * covered by a test here, since the repo's vitest run is node-only with no
   * DOM.
   *
   * Keyed on `index` because that is what changes when this row moves. When the
   * pressed arrow has just become disabled — the option reached the first or
   * last position — focus goes to the opposite arrow rather than being dropped
   * on the floor, which would strand the keyboard user mid-list.
   */
  useEffect(() => {
    const want = pendingFocus.current
    if (!want) return
    pendingFocus.current = null
    const primary = want === 'up' ? upRef.current : downRef.current
    const fallback = want === 'up' ? downRef.current : upRef.current
    const target = primary && !primary.disabled ? primary : fallback
    if (target && !target.disabled) target.focus()
  }, [index])

  // An option with an empty label still reorders and must still be operable by
  // keyboard, so the accessible name falls back to its position rather than
  // collapsing to `Move "" up` (AC2.5, AC4.5).
  const name = option.label.trim() ? `"${option.label}"` : `option ${index + 1}`

  return (
    <div
      ref={ref}
      className={cn('flex items-center gap-1.5', isDragging && 'opacity-50')}
    >
      {/* Always rendered, never hover-gated, so the affordance is discoverable
          (AC1.1). Inert rather than hidden when locked, so the row's controls
          don't reflow between a draft and an in-use form.

          Deliberately focusable: dnd-kit's SortableKeyboardPlugin starts a drag
          from a keydown on the focused handle, so a `tabIndex={-1}` here would
          both kill keyboard-initiated drags and leave screen-reader users a
          "Reorder …" button that does nothing. CanvasField's handle is
          focusable for the same reason — the two should not diverge. */}
      <button
        ref={locked ? undefined : handleRef}
        type='button'
        disabled={locked}
        aria-label={`Reorder ${name}`}
        className={cn(
          'h-8 w-5 shrink-0 flex items-center justify-center text-muted-foreground',
          locked ? 'opacity-40' : 'cursor-grab active:cursor-grabbing hover:text-foreground',
        )}
      >
        <GripVertical className='h-3.5 w-3.5' />
      </button>

      {/* Editing in place must keep working and must not be swallowed by the
          handle — which is why the handle is its own element (AC5.2).
          Deliberately NOT gated on `locked`: AC3.7 makes reordering read-only
          on an in-use version, and AC5.1/AC5.2 require the pre-existing
          add/remove/edit controls to behave exactly as before. Locking label
          editing here would be this ticket changing a neighbouring control. */}
      <Input
        value={option.label}
        onChange={e => onLabelChange(e.target.value)}
        placeholder={`Option ${index + 1}`}
        className='h-8 text-sm'
      />

      <div className='flex shrink-0'>
        <Button
          ref={upRef}
          size='icon'
          variant='ghost'
          className='h-8 w-7 text-muted-foreground hover:text-foreground'
          onClick={() => {
            pendingFocus.current = 'up'
            onMove(index, index - 1)
          }}
          disabled={locked || isFirst}
          aria-label={`Move ${name} up`}
        >
          <ChevronUp className='h-3.5 w-3.5' />
        </Button>
        <Button
          ref={downRef}
          size='icon'
          variant='ghost'
          className='h-8 w-7 text-muted-foreground hover:text-foreground'
          onClick={() => {
            pendingFocus.current = 'down'
            onMove(index, index + 1)
          }}
          disabled={locked || isLast}
          aria-label={`Move ${name} down`}
        >
          <ChevronDown className='h-3.5 w-3.5' />
        </Button>
      </div>

      <Button
        size='icon'
        variant='ghost'
        className='h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive'
        onClick={onRemove}
        // Unchanged from before this ticket — see the note on the label input.
        disabled={removeDisabled}
        aria-label='Remove option'
      >
        <X className='h-3.5 w-3.5' />
      </Button>
    </div>
  )
}
